import { useState, useRef, useEffect } from "react";
import { countyLabel } from "@/lib/county-label";
import { Link } from "@tanstack/react-router";
import { LocateFixed, Search } from "lucide-react";
import { ALL_US_STATES, countyPagesEnabled, getStateByAbbr, type StateInfo } from "@/lib/states";
import { riskLabel } from "@/lib/risk";
import { looksLikeZip, resolveQuery, uncoveredZipPlace, type LocationMatch } from "@/lib/location";
import type { PlaceAnswer } from "@/lib/place-search";
import { track } from "@/lib/analytics";
import { RiskSnapshot } from "./risk-snapshot";
import { CountyRecordLine } from "./county-record-line";
import { SourceList } from "./source-list";
import { ShareCard } from "./share-card";
import { Checklist } from "@/components/checklist";
import { cn } from "@/lib/utils";

interface Result {
  resolved: LocationMatch | null;
  state?: StateInfo;
  query: string;
  /** Set when the answer came from the device's location rather than typed text. */
  fromLocation?: boolean;
  /** Answered while typing: don't scroll the page out from under the keyboard. */
  auto?: boolean;
  /** For a ZIP answer whose county was found: false when the ZIP spans counties. */
  zipWhole?: boolean;
  /** For a town: which county it is in, and any others it reaches. */
  placeNote?: string;
  /** Other places with the same name, offered under a well-known city's answer. */
  alternatives?: PlaceAnswer[];
  /** Several places match and none stands out: the reader picks. */
  choices?: PlaceAnswer[];
  /** A town or county name is being looked up. */
  pending?: boolean;
}

/** Enough for every state's Washington County (there are 30), not so many it scrolls forever. */
const MAX_CHOICES = 40;

/** "Austin" from "Austin, TX"; "Travis County" for a county. */
function placeName(a: PlaceAnswer): string {
  return a.match.method === "county"
    ? countyLabel({ ...a.match.county, stateAbbr: a.match.stateAbbr })
    : a.match.label.replace(/, [A-Z]{2}$/, "");
}

type LocateState = "idle" | "locating" | "denied" | "unavailable" | "outside";

const LOCATE_MESSAGE: Record<Exclude<LocateState, "idle" | "locating">, string> = {
  denied: "Location is turned off for this site. Type your ZIP code instead.",
  unavailable: "Your device couldn't find its location just now. Type your ZIP code instead.",
  outside: "That location isn't in a U.S. state we cover. The bite steps still apply anywhere.",
};

/**
 * Where the answer came from, in one line. The card under it is about the state; when the
 * county is known, CDC's record for it is set above the card, and this line says what
 * the card covers.
 */
function locationNote(
  resolved: LocationMatch | null,
  stateName: string,
  fromLocation = false,
): string | undefined {
  if (!resolved || (resolved.method === "state" && !fromLocation)) return undefined;
  if (resolved.county) return `This card covers all of ${stateName}.`;
  const place = fromLocation
    ? "Your location"
    : resolved.method === "zip"
      ? `ZIP ${resolved.query.trim()}`
      : resolved.label.split(",")[0];
  return `${place} is in ${stateName}. This card covers the whole state.`;
}

export function CheckArea({
  compact = false,
  onAnswer,
  initialQuery,
}: {
  compact?: boolean;
  /** A search to run as soon as the page loads, e.g. from "/states?q=Brandon, FL". */
  initialQuery?: string;
  /** Told the answered state's abbreviation (or null), so a map beside it can follow. */
  onAnswer?: (stateAbbr: string | null) => void;
}) {
  const [query, setQuery] = useState("");
  const [result, setResult] = useState<Result | null>(null);
  const [locate, setLocate] = useState<LocateState>("idle");
  const [canLocate, setCanLocate] = useState(false);
  const resultRef = useRef<HTMLDivElement>(null);

  // A search handed over in the address runs once, after the page has loaded.
  const ranInitial = useRef<string | null>(null);
  useEffect(() => {
    if (!initialQuery || ranInitial.current === initialQuery) return;
    ranInitial.current = initialQuery;
    setQuery(initialQuery);
    answer(initialQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once per query handed over
  }, [initialQuery]);

  // Only offer the button where the browser can actually do it (and only after mount,
  // so the server-rendered HTML and the first client render agree).
  useEffect(() => {
    setCanLocate(typeof navigator !== "undefined" && "geolocation" in navigator);
  }, []);

  useEffect(() => {
    onAnswer?.(result?.state?.abbr ?? null);
  }, [result, onAnswer]);

  // A ZIP answer appears at once from the state table; the county follows a moment later
  // from the Census ZIP areas, loaded on the device only now (src/lib/zip-county.ts).
  const zipQuery =
    result?.resolved?.method === "zip" && !result.resolved.county ? result.query : null;
  const zipState = result?.state?.abbr;
  useEffect(() => {
    if (!zipQuery || !zipState) return;
    let live = true;
    (async () => {
      const { countyForZip } = await import("@/lib/zip-county");
      const hit = await countyForZip(zipQuery);
      if (!hit || !live) return;
      const { loadCountyIndex } = await import("@/lib/geo/county-shapes");
      const entry = (await loadCountyIndex()).find((e) => e.fips === hit.fips);
      // A ZIP area that crosses a state line can map to the neighbour; keep the state's answer.
      if (!entry || entry.stateAbbr !== zipState || !live) return;
      setResult((r) =>
        r && r.query === zipQuery && r.resolved
          ? {
              ...r,
              zipWhole: hit.whole,
              resolved: {
                ...r.resolved,
                county: { fips: entry.fips, name: entry.name, slug: entry.slug },
              },
            }
          : r,
      );
    })();
    return () => {
      live = false;
    };
  }, [zipQuery, zipState]);

  // Bring the result into view after a search (no-op if it's already visible,
  // thanks to block: "nearest") and let screen readers announce it via aria-live.
  useEffect(() => {
    if (result && !result.auto)
      resultRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    // Picking a place removes the button that was picked, so keyboard and screen-reader
    // focus is moved to what replaced it rather than dropping to the top of the page.
    if (focusNext.current === "result") resultRef.current?.focus({ preventScroll: true });
    if (focusNext.current === "choices")
      document.getElementById("place-choices")?.focus({ preventScroll: true });
    focusNext.current = null;
  }, [result]);

  // Each search gets a number, so a slow name lookup can't overwrite a newer answer.
  const searchId = useRef(0);
  const focusNext = useRef<"result" | "choices" | null>(null);

  const show = (r: Result, method: string) => {
    setResult(r);
    track("area_search_submitted", {
      query: r.query,
      method,
      state: r.resolved?.stateName ?? null,
      matched: Boolean(r.state),
      auto: Boolean(r.auto),
    });
  };

  const showPlace = (
    a: PlaceAnswer,
    query: string,
    alternatives?: PlaceAnswer[],
    picked = false,
  ) => {
    if (picked) {
      ++searchId.current;
      focusNext.current = "result";
    }
    show(
      {
        resolved: a.match,
        state: a.match.state,
        query,
        placeNote: a.note,
        alternatives,
      },
      a.match.method,
    );
  };

  const answer = (text: string, auto = false) => {
    const id = ++searchId.current;
    const quick = resolveQuery(text);
    setLocate("idle");
    // ZIP codes and state names are answered from tables already on the page.
    if (!quick ? looksLikeZip(text) || !text.trim() : quick.method !== "city") {
      show(
        { resolved: quick, state: quick?.state, query: text, auto },
        quick?.method ?? "unresolved",
      );
      return;
    }
    // A town or county name: look it up in the Census place records, fetched only now.
    setResult({ resolved: null, query: text, auto, pending: true });
    void (async () => {
      let answers: PlaceAnswer[] = [];
      let clear = false;
      try {
        const [{ findPlaces, isClearWinner }, { loadCountyIndex }] = await Promise.all([
          import("@/lib/place-search"),
          import("@/lib/geo/county-shapes"),
        ]);
        answers = await findPlaces(text, await loadCountyIndex());
        clear = isClearWinner(text, answers);
      } catch {
        // Offline or a failed chunk: fall back to the state-level answer below.
      }
      if (id !== searchId.current) return;
      if (answers.length && clear) {
        showPlace(answers[0], text, answers.slice(1, 9));
      } else if (answers.length) {
        focusNext.current = "choices";
        show(
          { resolved: null, query: text, auto, choices: answers.slice(0, MAX_CHOICES) },
          "choices",
        );
      } else {
        show(
          { resolved: quick, state: quick?.state, query: text, auto },
          quick?.method ?? "unresolved",
        );
      }
    })();
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    answer(query);
  };

  // A complete ZIP needs no button press: answer the moment the fifth digit lands.
  const onChange = (value: string) => {
    setQuery(value);
    if (/^\d{5}$/.test(value.trim()) && resolveQuery(value)?.state) answer(value, true);
  };

  // The coordinates never leave the device: they are compared with a table of county
  // centroids that is only downloaded now, when the reader asks (src/lib/nearest-county.ts).
  const useLocation = () => {
    if (!navigator.geolocation) return;
    // A name lookup still running must not overwrite the location's answer.
    ++searchId.current;
    setLocate("locating");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { findNearbyCounty } = await import("@/lib/nearest-county");
        const county = await findNearbyCounty(pos.coords.latitude, pos.coords.longitude);
        const state = county ? getStateByAbbr(county.stateAbbr) : undefined;
        if (!county || !state) {
          setLocate("outside");
          setResult(null);
          return;
        }
        setLocate("idle");
        setQuery("");
        setResult({
          fromLocation: true,
          query: "your location",
          state,
          resolved: {
            method: "county",
            label: `${countyLabel(county)}, ${state.abbr}`,
            stateAbbr: state.abbr,
            stateName: state.name,
            state,
            county: { fips: county.fips, name: county.name, slug: county.slug },
            query: "your location",
          },
        });
        track("area_search_submitted", {
          query: "",
          method: "geolocation",
          state: state.name,
          matched: true,
        });
      },
      (err) => setLocate(err.code === err.PERMISSION_DENIED ? "denied" : "unavailable"),
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 },
    );
  };

  return (
    <div id="check" className={compact ? "" : "border-t border-rule/70 pt-6"}>
      {!compact && (
        <>
          <h2 className="font-display text-[1.75rem] font-medium leading-tight text-foreground">
            Check your area
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Enter a ZIP code, town, county or state to see whether Lone Star ticks live there and
            when they peak.
          </p>
        </>
      )}

      <form
        onSubmit={onSubmit}
        className={cn("flex flex-col gap-2 sm:flex-row sm:gap-0", !compact && "mt-4")}
      >
        <label htmlFor="check-input" className="sr-only">
          ZIP code, town, county or state
        </label>
        <div className="relative flex-1">
          <Search
            className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <input
            id="check-input"
            list="state-options"
            value={query}
            onChange={(e) => onChange(e.target.value)}
            inputMode="search"
            placeholder="e.g. 30301 or Brandon, FL"
            autoComplete="postal-code"
            className="h-[3.25rem] w-full rounded-sm border-[1.5px] border-foreground/70 bg-card pl-10 pr-3 text-base text-foreground outline-none placeholder:text-muted-foreground/80 focus:border-primary focus:ring-2 focus:ring-primary/25 sm:rounded-r-none sm:border-r-0"
          />
          <datalist id="state-options">
            {ALL_US_STATES.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
        </div>
        <button
          type="submit"
          className="inline-flex h-[3.25rem] items-center justify-center rounded-sm bg-primary px-7 text-[0.9375rem] font-semibold text-primary-foreground hover:bg-primary-dark sm:rounded-l-none"
        >
          Check my area
        </button>
      </form>

      {canLocate && (
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          <button
            type="button"
            onClick={useLocation}
            disabled={locate === "locating"}
            className="inline-flex items-center gap-1.5 font-semibold text-primary underline decoration-primary/40 underline-offset-4 hover:decoration-current disabled:opacity-60"
          >
            <LocateFixed className="h-4 w-4" aria-hidden />
            {locate === "locating" ? "Finding you…" : "Use my location"}
          </button>
          <span className="text-xs text-muted-foreground">It stays on this device.</span>
          {locate !== "idle" && locate !== "locating" && (
            <p role="status" className="w-full text-sm text-muted-foreground">
              {LOCATE_MESSAGE[locate]}
            </p>
          )}
        </div>
      )}

      {result && (
        <div
          ref={resultRef}
          tabIndex={-1}
          // A list of up to 40 places is not read out; focus moves to its heading instead.
          aria-live={result.choices ? "off" : "polite"}
          className="mt-6 space-y-5 outline-none"
        >
          {result.pending ? (
            <p role="status" className="text-[0.9375rem] text-muted-foreground">
              Looking up &ldquo;{result.query.trim()}&rdquo;&hellip;
            </p>
          ) : result.choices ? (
            <PlaceChoices
              choices={result.choices}
              onPick={(a) => showPlace(a, result.query, undefined, true)}
            />
          ) : result.state ? (
            <>
              {result.resolved?.county && (
                <CountyRecordLine
                  county={result.resolved.county}
                  stateAbbr={result.state.abbr}
                  stateSlug={result.state.slug}
                  pageEnabled={countyPagesEnabled(result.state)}
                  note={
                    result.resolved.method === "zip"
                      ? `ZIP ${result.query.trim()} is ${result.zipWhole === false ? "mostly in" : "in"} ${countyLabel({ ...result.resolved.county, stateAbbr: result.state.abbr })}, by the Census Bureau's ZIP areas.`
                      : result.placeNote
                  }
                />
              )}

              {result.alternatives && result.alternatives.length > 0 && (
                <p className="text-sm text-muted-foreground">
                  Another {placeName(result.alternatives[0])}?{" "}
                  {result.alternatives.map((a, i) => (
                    <span key={`${a.match.stateAbbr}-${a.match.county.fips}`}>
                      {i > 0 && " · "}
                      <button
                        type="button"
                        onClick={() => showPlace(a, result.query, undefined, true)}
                        aria-label={`${placeName(a)}, ${a.match.stateName}`}
                        className="link font-semibold"
                      >
                        {a.match.method === "county" ? a.match.label : a.match.stateName}
                      </button>
                    </span>
                  ))}
                </p>
              )}

              <RiskSnapshot
                state={result.state}
                dense={compact}
                showLink
                locationNote={locationNote(result.resolved, result.state.name, result.fromLocation)}
              />

              {compact && (
                <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                  <Link
                    to="/tick-bite-tool"
                    className="inline-flex rounded-sm bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary-dark"
                  >
                    What to do after a bite
                  </Link>
                  <Link to="/prevention" className="link text-sm font-semibold">
                    How to avoid bites
                  </Link>
                </div>
              )}

              {!compact && (
                <>
                  <section>
                    <h3 className="font-display text-xl font-medium text-foreground">
                      How to avoid bites in {result.state.name}
                    </h3>
                    <Checklist items={result.state.preventionNotes} className="mt-3" />
                  </section>

                  <p className="measure text-[0.9375rem] leading-relaxed text-muted-foreground">
                    Alpha-gal syndrome is a delayed allergy to red meat linked to some Lone Star
                    tick bites. Not every bite causes it, and only a clinician can diagnose it.{" "}
                    <Link to="/tick-bite-tool" className="link font-semibold">
                      What to do after a bite
                    </Link>{" "}
                    ·{" "}
                    <Link
                      to="/$guide"
                      params={{ guide: "alpha-gal-symptoms" }}
                      className="link font-semibold"
                    >
                      Alpha-gal symptoms
                    </Link>
                  </p>

                  <SourceList ids={result.state.sourceIds} title="Sources" collapsible />

                  <ShareCard
                    title={`Lone Star ticks in ${result.state.name}`}
                    subtitle={`Peak season: ${result.state.peakSeason}`}
                    level={result.state.risk}
                    kind="state"
                    state={result.state.slug}
                    summaryText={`Lone Star ticks in ${result.state.name}: ${riskLabel[result.state.risk]}\nPeak season: ${result.state.peakSeason}\n${result.state.summary}\nhttps://lonestarmap.com/states/${result.state.slug}`}
                    sharePath={`/states/${result.state.slug}`}
                    shareText={`Where Lone Star ticks live in ${result.state.name}, and what to do about them:`}
                  />
                </>
              )}
            </>
          ) : result.resolved ? (
            <div className="border-l-[3px] border-amber py-1 pl-4 text-sm text-foreground">
              <p className="font-semibold">{result.resolved.stateName}</p>
              <p className="mt-1">
                We couldn't load the {result.resolved.stateName} summary just now. Explore the{" "}
                <Link to="/map" className="font-semibold link">
                  national map
                </Link>
                , the{" "}
                <Link to="/prevention" className="font-semibold link">
                  prevention guide
                </Link>
                , or{" "}
                <Link to="/states" className="font-semibold link">
                  browse all states
                </Link>
                .
              </p>
            </div>
          ) : (
            <div className="border-l-[3px] border-rule/50 py-1 pl-4 text-[0.9375rem] leading-relaxed text-muted-foreground">
              {(() => {
                const uncovered = uncoveredZipPlace(result.query);
                if (uncovered) {
                  return (
                    <>
                      That ZIP code is in {uncovered}. We only cover the 50 states and DC, so we
                      have no answer for it. That is our gap, not a problem with what you typed. The{" "}
                      <Link to="/tick-bite-tool" className="font-semibold link">
                        bite steps
                      </Link>{" "}
                      and{" "}
                      <Link to="/tick-identification" className="font-semibold link">
                        identification guide
                      </Link>{" "}
                      still apply anywhere.
                    </>
                  );
                }
                return (
                  <>
                    We couldn&rsquo;t match &ldquo;{result.query}&rdquo; to a location.{" "}
                    {looksLikeZip(result.query)
                      ? "That looks like a ZIP code, but it isn't one we have a state for."
                      : "Try a 5-digit ZIP code, a town and its state, or a county."}{" "}
                    You can also{" "}
                    <Link to="/states" className="font-semibold link">
                      browse all states
                    </Link>
                    .
                  </>
                );
              })()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Several places share the name the reader typed and none is the obvious one: list them
 * with their state and county, and let the reader pick.
 */
function PlaceChoices({
  choices,
  onPick,
}: {
  choices: PlaceAnswer[];
  onPick: (a: PlaceAnswer) => void;
}) {
  const name = placeName(choices[0]);
  const first = choices[0].match;
  const countiesOnly = choices.every((a) => a.match.method === "county");
  // "Baltimore, MD" is a city and a county in one state: adding the state won't help.
  const oneState = choices.every((a) => a.match.stateAbbr === first.stateAbbr);
  return (
    <section aria-labelledby="place-choices">
      <h3
        id="place-choices"
        tabIndex={-1}
        className="font-display text-xl font-medium text-foreground outline-none"
      >
        Which {name}?
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        {choices.length === MAX_CHOICES ? `At least ${MAX_CHOICES}` : choices.length} places have
        that name.{" "}
        {oneState ? (
          "Pick yours."
        ) : (
          <>
            Pick yours, or add the state, like &ldquo;{name}, {first.stateAbbr}&rdquo;.
          </>
        )}
      </p>
      <ul
        className={cn(
          "mt-3 grid gap-x-6 border-t border-border",
          // County names repeat; only the state differs, so they fit two or three across.
          countiesOnly ? "grid-cols-2 lg:grid-cols-3" : "sm:grid-cols-2",
        )}
      >
        {choices.map((a) => (
          <li
            key={`${a.match.stateAbbr}-${a.match.county.fips}-${a.match.method}`}
            className="border-b border-border"
          >
            <button
              type="button"
              onClick={() => onPick(a)}
              className="flex min-h-11 w-full flex-wrap items-baseline justify-between gap-x-4 py-2.5 text-left transition-colors hover:text-primary"
            >
              <span className="font-semibold text-foreground">
                {countiesOnly ? a.match.stateName : `${placeName(a)}, ${a.match.stateName}`}
              </span>
              {a.match.method === "city" && (
                <span className="text-sm text-muted-foreground">
                  {countyLabel({ ...a.match.county, stateAbbr: a.match.stateAbbr })}
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

// Backwards-compatible alias (used by resource articles).
export const ZipChecker = CheckArea;
